import React from 'react';
import { cn } from '../../lib/utils';

// ─── Primitive pulse block ────────────────────────────────────────────────────
const Pulse = ({ className = '' }) => (
  <div
    className={cn(
      'animate-pulse rounded bg-neutral-200 dark:bg-neutral-800',
      'motion-reduce:animate-none',
      className,
    )}
    aria-hidden="true"
  />
);

// ─── Variant renderers ─────────────────────────────────────────────────────

const tableRows = (count) =>
  Array.from({ length: count }, (_, i) => (
    <div key={i} className="flex items-center gap-3 py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0 px-4">
      <Pulse className="h-4 w-4 rounded-full flex-shrink-0" />
      <Pulse className="h-4 flex-1" />
      <Pulse className="h-4 w-24" />
      <Pulse className="h-4 w-20" />
      <Pulse className="h-4 w-16" />
    </div>
  ));

const cardItems = (count) =>
  Array.from({ length: count }, (_, i) => (
    <div
      key={i}
      className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-5 space-y-3 bg-white dark:bg-neutral-900"
    >
      <div className="flex items-center gap-3">
        <Pulse className="h-10 w-10 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Pulse className="h-4 w-2/3" />
          <Pulse className="h-3 w-1/3" />
        </div>
      </div>
      <Pulse className="h-3 w-full" />
      <Pulse className="h-3 w-5/6" />
      <Pulse className="h-3 w-4/6" />
    </div>
  ));

const listItems = (count) =>
  Array.from({ length: count }, (_, i) => (
    <div key={i} className="flex items-center gap-3 py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
      <Pulse className="h-8 w-8 rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-1.5">
        <Pulse className="h-4 w-1/2" />
        <Pulse className="h-3 w-1/3" />
      </div>
      <Pulse className="h-6 w-16 rounded-full" />
    </div>
  ));

const formFields = (count) =>
  Array.from({ length: count }, (_, i) => (
    <div key={i} className="space-y-2">
      <Pulse className="h-3.5 w-24" />
      <Pulse className="h-10 w-full rounded-md" />
    </div>
  ));

const statsRow = (count) =>
  Array.from({ length: count }, (_, i) => (
    <div
      key={i}
      className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 space-y-3"
    >
      <div className="flex items-center justify-between">
        <Pulse className="h-3 w-20" />
        <Pulse className="h-10 w-10 rounded-lg" />
      </div>
      <Pulse className="h-8 w-28" />
      <Pulse className="h-3 w-24" />
    </div>
  ));

const pageFallback = () => (
  <div className="space-y-6">
    <div className="space-y-3">
      <Pulse className="h-4 w-32" />
      <Pulse className="h-8 w-64" />
      <Pulse className="h-3 w-96" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {statsRow(4)}
    </div>
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      {tableRows(6)}
    </div>
  </div>
);

const VARIANTS = {
  table: tableRows,
  card:  cardItems,
  list:  listItems,
  form:  formFields,
  stats: statsRow,
  page:  pageFallback,
};

const WRAPPERS = {
  card:  'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
  stats: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4',
};

/**
 * LoadingSkeleton — animated placeholder while data is loading.
 *
 * @param {object} props
 * @param {'table'|'card'|'list'|'form'|'stats'|'page'} [props.variant='list']
 * @param {number} [props.count=3]
 * @param {string} [props.className]
 */
const LoadingSkeleton = ({ variant = 'list', count = 3, className = '' }) => {
  const render = VARIANTS[variant] ?? VARIANTS.list;
  const wrapperClass = WRAPPERS[variant] || 'space-y-0';
  const content = variant === 'page' ? render() : render(count);

  return (
    <div
      role="status"
      aria-label="جار التحميل…"
      aria-busy="true"
      className={cn('w-full', wrapperClass, className)}
    >
      {content}
      <span className="sr-only">جار التحميل…</span>
    </div>
  );
};

export default LoadingSkeleton;
